import * as React from 'react';
import styled from 'styled-components';
import { Button, Icon, Sheet, Text } from '@amzn/storm-ui';
import { useIsRTL } from 'src/commons/hooks';
import { MobileNavSheet } from '../nav/components/MobileSideNav/MobileNavSheet';
import { IA2MobileAccountDropdown } from './components/AccountDropdown/IA2MobileAccountDropdown';
import { useAccountDropdownBundle } from 'src/providers/PantherLocalizationProvider';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { AccountSwitcherPublisher } from 'src/commons/utils/metrics';
import { publishCohortCounter, toCohort } from 'src/commons/utils/whaleCohort';
import { useAccountDropdownContext } from './components/AccountDropdown/AccountDropdownProvider';

type MobileAccountSwitcherOverlay = {
  isOpen: boolean;
  handleClose: () => void;
};

export const MobileAccountSwitcherCloseBtnContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.xxsmall.default};
  border-bottom: ${({ theme }) => theme.border.subtle.default};
`;

export const MobileAccountSwitcherIcon = styled(Icon)``;

export const SecondaryMobileMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const AllAccountsContainer = styled.div`
  text-align: center;
`;

export const MobileAccountSwitcherOverlay: React.FC<
  MobileAccountSwitcherOverlay
> = ({ isOpen, handleClose: onClose }) => {
  const { isPrecomputed } = useAccountDropdownContext();
  const handleClose = () => {
    publishCohortCounter(
      AccountSwitcherPublisher,
      'Close',
      toCohort(isPrecomputed),
    );
    onClose();
  };
  const isRtl = useIsRTL();
  const bundle = useAccountDropdownBundle();
  const chromeConfig = useChromeConfigContext();
  const isCurrentScopeAllAccounts =
    chromeConfig.pageHeader?.pageScope === 'ALL_ACCOUNTS';
  const supportedPageScopes = chromeConfig.pageHeader?.supportedPageScopes;
  const isSwitchable =
    Array.isArray(supportedPageScopes) && supportedPageScopes.length > 1;

  return (
    <>
      {isCurrentScopeAllAccounts && !isSwitchable && (
        <Sheet
          isOpen={isOpen}
          onClose={handleClose}
          closeButton={{
            type: 'icon',
            label: 'close',
          }}
          side="bottom"
          sheetElementId="AACChromePortal"
        >
          <AllAccountsContainer>
            <Text>{bundle.getMessage('global-accounts-pill-message')}</Text>
          </AllAccountsContainer>
        </Sheet>
      )}
      {(!isCurrentScopeAllAccounts || isSwitchable) && (
        <MobileNavSheet
          isOpen={isOpen}
          onClose={handleClose}
          closeButton={{
            type: 'icon',
            label: 'close',
          }}
          type="light"
          side="start"
          withCloseButton={false}
          hasInnerPadding={false}
          sheetElementId="AACChromePortal"
        >
          <SecondaryMobileMenuContainer>
            <MobileAccountSwitcherCloseBtnContainer>
              <Button
                onClick={handleClose}
                variant="transparent"
                role="button"
                data-ccx-e2e-id="aac-mobile-account-switcher-done-button"
              >
                <MobileAccountSwitcherIcon
                  type={`angle-${isRtl ? 'right' : 'left'}`}
                />
                {bundle.getMessage('account-switcher-done-label')}
              </Button>
            </MobileAccountSwitcherCloseBtnContainer>
            <IA2MobileAccountDropdown />
          </SecondaryMobileMenuContainer>
        </MobileNavSheet>
      )}
    </>
  );
};
